import { useEffect, useState } from 'react';
import type { ClientSession } from '../api';
type Draft = {
  message: string;
  linkUrl: string;
  linkLabel: string;
  startsAt: string | null;
  endsAt: string | null;
  enabled: boolean;
  version: number;
};
function localTime(value: string | null) {
  if (!value) return '';
  const date = new Date(value);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}
export function AnnouncementPage({ session }: { session: ClientSession | null }) {
  const [draft, setDraft] = useState<Draft | null>(null);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try {
      const r = await fetch('/api/admin/announcement');
      const d = (await r.json()) as Draft & { error?: string };
      if (!r.ok) throw new Error(d.error);
      setDraft(d);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load announcement');
    }
  };
  useEffect(() => {
    void load();
  }, []);
  if (session?.user?.role !== 'admin')
    return (
      <main className="center-page">
        <h1>Administrator required</h1>
      </main>
    );
  const update = (patch: Partial<Draft>) => {
    setDraft((d) => (d ? { ...d, ...patch } : d));
    setSaved(false);
  };
  return (
    <main className="settings-page">
      <a href="/admin">Back to administration</a>
      <h1>Announcement bar</h1>
      <p>
        A public notice across the app. Set optional start and end times in your local timezone.
      </p>
      {error && (
        <p role="alert">
          {error} <button onClick={() => void load()}>Reload</button>
        </p>
      )}
      {draft && (
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            setError('');
            setSaved(false);
            try {
              const r = await fetch('/api/admin/announcement', {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  'X-CSRF-Token': session.csrfToken || '',
                },
                body: JSON.stringify(draft),
              });
              const d = (await r.json()) as { version: number; error?: string };
              if (!r.ok) throw new Error(d.error);
              setDraft({ ...draft, version: d.version });
              setSaved(true);
            } catch (e) {
              setError(e instanceof Error ? e.message : 'Could not save announcement');
            } finally {
              setBusy(false);
            }
          }}
        >
          <fieldset disabled={busy}>
            <legend>Notice and schedule</legend>
            <label className="field">
              <span>Announcement text</span>
              <textarea
                maxLength={500}
                value={draft.message}
                onChange={(e) => update({ message: e.target.value })}
              />
            </label>
            <label className="field">
              <span>Link URL (optional)</span>
              <input
                type="url"
                placeholder="https://"
                value={draft.linkUrl}
                onChange={(e) => update({ linkUrl: e.target.value })}
              />
            </label>
            <label className="field">
              <span>Link label</span>
              <input
                maxLength={80}
                value={draft.linkLabel}
                onChange={(e) => update({ linkLabel: e.target.value })}
              />
            </label>
            <div className="form-grid">
              {(['startsAt', 'endsAt'] as const).map((key) => (
                <label className="field" key={key}>
                  <span>{key === 'startsAt' ? 'Starts at (optional)' : 'Ends at (optional)'}</span>
                  <input
                    type="datetime-local"
                    value={localTime(draft[key])}
                    onChange={(e) =>
                      update({
                        [key]: e.target.value
                          ? Number.isFinite(new Date(e.target.value).getTime())
                            ? new Date(e.target.value).toISOString()
                            : null
                          : null,
                      })
                    }
                  />
                </label>
              ))}
            </div>
            <label className="check-field">
              <input
                type="checkbox"
                checked={draft.enabled}
                onChange={(e) => update({ enabled: e.target.checked })}
              />
              Enable announcement according to this schedule
            </label>
            <p>Preview</p>
            <aside className="announcement-bar">
              <span>{draft.message || 'Your announcement text'}</span>
              <span>{draft.linkLabel}</span>
            </aside>
            <button className="button" type="submit">
              {busy ? 'Saving…' : 'Save announcement'}
            </button>
          </fieldset>
          {saved && <p role="status">Announcement saved.</p>}
        </form>
      )}
    </main>
  );
}
